import { ReactElement, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import config from 'const/config'
import queryClient from 'const/queryClient'
import routes from 'const/routes'
import { useAutoRegisterUpdate, useCreditAccounts } from 'const/syfApiQueries'
import getAutoRegisterPayload from 'helpers/getAutoRegisterPayload'
import getLanguageFromStorage from 'helpers/getLanguageFromStorage'
import noop from 'helpers/noop'
import { setAnalyticsDefaults } from 'helpers/triggerAnalyticsEvent'
import postElectronicConsent from 'services/postElectronicConsent'
import i18n from 'strings/i18n'
import { creditAccountsCacheKey } from '@syf-api-library/credit-accounts'
import { Button as BrandRefreshButton } from '@syf-cl/button'
import CLButton from 'syf-component-library/ui/atoms/Button/Button'
import Link from 'syf-component-library/ui/atoms/Link/Link'
import { Inline, Inset, Stack } from 'syf-component-library/ui/spacing'
import { Body, H5 } from 'syf-component-library/ui/typography'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'
import AccountManagerTreeGraphic from 'ui/atoms/AccountManagerTreeGraphic'

import { ButtonWrapper, SimpleModal } from './subcomponents'

const WelcomeInterstitial = (): ReactElement => {
  const headerRef = useRef(null)
  const navigate = useNavigate()
  const lang = getLanguageFromStorage()
  const [isOpen, setIsOpen] = useState(true)

  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const { mutate: mutateAutoRegister } = useAutoRegisterUpdate()

  useEffect(() => {
    const analyticsData = {
      PageSubFunction: 'econsent'
    }
    setAnalyticsDefaults(analyticsData)
  }, [])

  const handleElectronicConsent = async () => {
    setIsOpen(false)
    try {
      const leadAccountId = accounts?.[0]?.info?.['cipher.accountId']

      const econsentStatus = await postElectronicConsent(leadAccountId)

      if (!econsentStatus) {
        navigate(routes.ERROR)
        return
      }

      const accountsToAutoRegister = getAutoRegisterPayload(accounts)

      // Updating credit accounts data with new econsent status.
      queryClient.invalidateQueries(creditAccountsCacheKey())

      if (accountsToAutoRegister.length) {
        mutateAutoRegister(accountsToAutoRegister, {
          onSuccess: () =>
            queryClient.invalidateQueries(creditAccountsCacheKey())
        })
      }
    } catch (error) {
      navigate(routes.ERROR)
    }
  }

  const brandRefreshEnabled = isBrandRefreshEnabled()
  const AgreeButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : CLButton
  const variantProps = brandRefreshEnabled
    ? { variant: 'tertiary' }
    : { buttonType: 'text' }

  return isOpen ? (
    <SimpleModal
      id="simple-modal"
      isOpen={isOpen}
      headerRef={headerRef}
      aria-label={`${i18n({ welcomeInterstitial: 'welcomeTitle' })}`}
      aria-describedby={`${i18n({
        welcomeInterstitial: 'manageAccountSubTitle'
      })}`}
      // Interstitial is non-dismissible; only "Agree & Continue" may close it.
      escapeModalHandler={noop}
      outsideClickHandler={noop}
      data-test="consent-modal"
      data-cy="consent-modal"
    >
      <Inset all="xlarge" respondToBreakpoints={['small']}>
        <H5 weight="bold">
          {`${i18n({ welcomeInterstitial: 'welcomeTitle' })}`}
        </H5>
        <Body>{`${i18n({
          welcomeInterstitial: 'manageAccountSubTitle'
        })}`}</Body>
        <AccountManagerTreeGraphic />

        <Inset vertical="large" hideOnBreakpointDown="medium">
          <div>
            {i18n(
              { welcomeInterstitial: 'eConsentStatement' },
              {
                legalStatementLink: (
                  <Link
                    isInline
                    href={`${config.ASSETS_HOST}/assets/generic/${lang}/eConsent_document.pdf`}
                    target="_blank"
                    data-type="welcome view"
                    data-reason="eConsent file"
                    data-object="link"
                    data-analytics="clicktrackingevent"
                  >
                    {i18n({
                      welcomeInterstitial: 'legalStatement'
                    })}
                  </Link>
                )
              }
            )}
          </div>
          <Stack size="large" />
          <div data-cy="welcome-note">
            <Body isInline weight="bold">
              {`${i18n({
                welcomeInterstitial: 'note'
              })}`}
            </Body>
            <Inline size="small" />
            {i18n({ welcomeInterstitial: 'welcomeNote' })}
          </div>
        </Inset>

        <Stack size="large" />
        <ButtonWrapper key="modal-button">
          <AgreeButton
            {...variantProps}
            onClick={handleElectronicConsent}
            data-cy="agree-continue-button"
            data-type="welcome view"
            data-analytics="clicktrackingevent"
            data-reason="agree & continue"
            data-test="agree-button"
          >
            {i18n({
              button: 'agreeAndContinue'
            })}
          </AgreeButton>
        </ButtonWrapper>
      </Inset>
    </SimpleModal>
  ) : null
}

export default WelcomeInterstitial
